<script lang="ts">
  import { memberStatusMeta, timeUntil, type MemberRenewal } from './member';
  import { agoShort, calendarShort, fullDate } from './member';

  // THE renewal cell — the members table and the win-back queue both read the
  // membership clock through here, so the same member can't show two different
  // dates on two screens. Sits directly after PlanCell on both: what they pay, then
  // when it next moves.
  //
  // Same two-slot anatomy as PlanCell — VALUE on top, QUALIFIER beneath — so the
  // billing pair reads as one block rather than two columns that happen to be
  // adjacent. Here the qualifier is the verb, and it's what makes the date legible:
  // "12 Aug · renews" and "12 Aug · lapses" are opposite facts in the same shape.
  //
  // Exactly one line carries the status colour, and which one is the point:
  //   · a cancelling member  → the COUNTDOWN is coloured (act before it runs out)
  //   · someone already gone → the VERB is coloured (it happened; the date is history)
  //   · a routine renewal    → nothing is coloured
  // so the only tinted rows in the column are the ones a creator can still act on.
  // `align` mirrors PlanCell's, and for the same reason: passing `items-start` in a
  // class string wouldn't reliably beat the `items-end` default.
  let {
    row,
    nowMs = Date.now(),
    align = 'right',
  }: { row: MemberRenewal; nowMs?: number; align?: 'left' | 'right' } = $props();

  type Clock = { top: string; sub: string; color: string; on: 'top' | 'sub' | 'none'; title: string };

  const clock = $derived.by<Clock | null>(() => {
    const meta = memberStatusMeta(row.status);
    if (meta?.departed) {
      const when = row.departed_at ?? row.ends_at;
      return {
        top: agoShort(when), sub: meta.verb, color: meta.color, on: 'sub',
        title: when ? `${meta.label} · ${fullDate(when)}` : meta.label,
      };
    }
    if (meta && row.status === 'cancelling') {
      return {
        top: row.ends_at ? timeUntil(row.ends_at, nowMs) : 'cancelled', sub: meta.verb,
        color: meta.color, on: 'top',
        title: row.ends_at
          ? `Cancelled — lapses ${fullDate(row.ends_at)}`
          : 'Cancelled — no end date reported',
      };
    }
    if (row.ends_at) {
      return {
        top: calendarShort(row.ends_at), sub: 'renews', color: '', on: 'none',
        title: `Renews ${fullDate(row.ends_at)} · ${timeUntil(row.ends_at, nowMs)}`,
      };
    }
    return null;
  });
</script>

<div
  class="flex flex-col gap-0.5 leading-none {align === 'left' ? 'items-start' : 'items-end'}"
  title={clock?.title ?? 'No renewal date'}
>
  {#if clock}
    <span class="font-mono text-xs" style={clock.on === 'top' ? `color:${clock.color}` : ''}>{clock.top}</span>
    <span
      class={'text-[0.65rem] ' + (clock.on === 'sub' ? '' : 'text-base-content/60')}
      style={clock.on === 'sub' ? `color:${clock.color}` : ''}
    >{clock.sub}</span>
  {:else}
    <!-- Nothing bills and nothing has ended: a free member, or a community we can't
         sweep. PlanCell to the left already says which — this stays quiet. -->
    <span class="font-mono text-xs text-base-content/40">—</span>
  {/if}
</div>
